import { useState } from "react";
import { useAuth } from "@/App";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Mail, Lock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AuthPage() {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [registeredMsg, setRegisteredMsg] = useState(null);
  const [pendingEmail, setPendingEmail] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);

  const handleResend = async () => {
    if (!pendingEmail) return;
    setLoading(true);
    try {
      await api.post("/auth/resend-verification", { email: pendingEmail });
      toast.success("Se a conta existir e ainda não estiver confirmada, enviámos um novo email.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível reenviar o email.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData(e.target);
    try {
      await api.post("/auth/forgot-password", { email: fd.get("email") });
      setForgotSent(true);
      toast.success("Se existir uma conta com esse email, enviámos um link de recuperação.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível enviar o email. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    const fd = new FormData(e.target);
    const email = fd.get("email");
    try {
      const res = await api.post("/auth/login", {
        email,
        password: fd.get("password"),
      });
      login(res.data.token, res.data.user);
      toast.success("Bem-vindo de volta!");
    } catch (err) {
      if (err.response?.status === 403) {
        setPendingEmail(email);
        setNeedsVerification(true);
      }
      toast.error(err.response?.data?.detail || "Erro ao entrar");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData(e.target);
    const email = fd.get("email");
    try {
      const res = await api.post("/auth/register", {
        name: fd.get("name"),
        email,
        password: fd.get("password"),
      });
      if (res.data?.requires_verification) {
        setPendingEmail(email);
        setRegisteredMsg(res.data.message || "Conta criada! Confirme o seu email para entrar.");
        toast.success("Verifique o seu email para confirmar a conta.");
      } else if (res.data?.token) {
        login(res.data.token, res.data.user);
        toast.success("Conta criada com sucesso!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 paper-texture">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center gap-2.5 mb-10 text-primary">
            <BookOpen className="h-9 w-9" />
            <span className="font-heading font-bold text-3xl tracking-tight">EduGrade</span>
          </div>

          {registeredMsg ? (
            <Card className="border-0 shadow-lg" data-testid="verify-sent-card">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading text-xl">Confirme o seu email</CardTitle>
                <CardDescription>{registeredMsg}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="verify-sent-message">
                    Enviámos um link de confirmação para <strong>{pendingEmail}</strong>. Verifique a sua caixa de entrada (e a pasta de spam). O link é válido durante 24 horas.
                  </div>
                  <Button type="button" variant="outline" className="w-full" onClick={handleResend} disabled={loading} data-testid="verify-resend-btn">
                    {loading ? "A enviar..." : "Reenviar email de confirmação"}
                  </Button>
                  <button type="button" onClick={() => { setRegisteredMsg(null); setNeedsVerification(false); }} className="w-full text-center text-sm text-muted-foreground hover:text-primary hover:underline" data-testid="verify-back-btn">
                    Voltar a entrar
                  </button>
                </div>
              </CardContent>
            </Card>
          ) : showForgot ? (
            <Card className="border-0 shadow-lg" data-testid="forgot-card">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading text-xl">Recuperar palavra-passe</CardTitle>
                <CardDescription>
                  {forgotSent
                    ? "Se existir uma conta com esse email, receberá um link para repor a palavra-passe."
                    : "Introduza o seu email e enviaremos um link para repor a palavra-passe."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {forgotSent ? (
                  <div className="space-y-4">
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="forgot-sent-message">
                      Verifique a sua caixa de entrada (e a pasta de spam). O link é válido durante 1 hora.
                    </div>
                    <Button type="button" variant="outline" className="w-full" onClick={() => setShowForgot(false)} data-testid="forgot-back-btn">
                      Voltar a entrar
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgot} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="forgot-email" name="email" type="email" placeholder="professor@escola.pt" className="pl-9" required data-testid="forgot-email" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full active:scale-95 transition-transform" disabled={loading} data-testid="forgot-submit">
                      {loading ? "A enviar..." : "Enviar link de recuperação"}
                    </Button>
                    <button type="button" onClick={() => setShowForgot(false)} className="w-full text-center text-sm text-muted-foreground hover:text-primary hover:underline" data-testid="forgot-cancel">
                      Voltar
                    </button>
                  </form>
                )}
              </CardContent>
            </Card>
          ) : (
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registar</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-xl">Bem-vindo de volta</CardTitle>
                  <CardDescription>Aceda à sua plataforma de correção</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="login-email" name="email" type="email" placeholder="professor@escola.pt" className="pl-9" required data-testid="login-email" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Palavra-passe</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="login-password" name="password" type="password" placeholder="Min. 6 caracteres" className="pl-9" required data-testid="login-password" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full active:scale-95 transition-transform" disabled={loading} data-testid="login-submit">
                      {loading ? "A entrar..." : "Entrar"}
                    </Button>
                    <button type="button" onClick={() => { setShowForgot(true); setForgotSent(false); }} className="w-full text-center text-sm text-primary hover:underline mt-1" data-testid="forgot-password-link">
                      Esqueci-me da palavra-passe
                    </button>
                    {needsVerification && (
                      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 space-y-2" data-testid="login-verify-notice">
                        <p>Precisa de confirmar o seu email antes de entrar.</p>
                        <Button type="button" variant="outline" size="sm" className="w-full" onClick={handleResend} disabled={loading} data-testid="login-resend-btn">
                          {loading ? "A enviar..." : "Reenviar email de confirmação"}
                        </Button>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-xl">Criar conta</CardTitle>
                  <CardDescription>Comece a corrigir testes com IA</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nome</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="register-name" name="name" placeholder="Prof. Maria Silva" className="pl-9" required data-testid="register-name" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="register-email" name="email" type="email" placeholder="professor@escola.pt" className="pl-9" required data-testid="register-email" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Palavra-passe</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input id="register-password" name="password" type="password" placeholder="Min. 6 caracteres" className="pl-9" minLength={6} required data-testid="register-password" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full active:scale-95 transition-transform" disabled={loading} data-testid="register-submit">
                      {loading ? "A criar conta..." : "Criar conta"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          )}

          <p className="text-xs text-muted-foreground text-center mt-6">
            Ao criar conta ou entrar, concorda com a nossa{" "}
            <Link to="/privacidade" className="text-primary hover:underline" data-testid="privacy-link-auth">Política de Privacidade</Link>.
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1760009229725-7ef1990e585f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBlZHVjYXRpb24lMjBzY2hvb2wlMjBzdXBwbGllc3xlbnwwfHx8fDE3NzQzODQyNjR8MA&ixlib=rb-4.1.0&q=85"
          alt="Education"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/5" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="bg-black/40 backdrop-blur-md rounded-2xl p-8">
            <h2 className="font-heading font-bold text-2xl mb-2">Correção inteligente de testes</h2>
            <p className="text-white/80 text-sm leading-relaxed">
              Carregue os testes dos seus alunos e deixe a IA fazer a correção.
              Revise, edite e exporte o mapa de avaliação em segundos.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
